import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, User } from '../contexts/AuthContext';
import { signInUser, SigninCredentials, ApiErrorResponse, SigninResponse } from '../services/authService';
import styles from './LoginPage.module.css';

const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    const credentials: SigninCredentials = { username, password };

    try {
      const response: SigninResponse = await signInUser(credentials); // Actual API call
      
      // The UserAuthResponse from authService needs to be mapped to User type for AuthContext if they differ.
      // Assuming they are compatible for now or UserAuthResponse is a superset of User.
      // If UserAuthResponse is directly compatible with AuthContext's User type:
      const userForContext: User = response.user; 

      await login(response.token, userForContext); 
      const isAdmin = userForContext?.role?.role_name?.toLowerCase() === 'admin';
      navigate(isAdmin ? '/dashboard' : '/select-store'); 

    } catch (err) { // err should be ApiErrorResponse if thrown by authService
      const apiError = err as ApiErrorResponse;
      setError(apiError.message || 'Login failed. Please check your credentials or try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.loginPageContainer}>
      <div className={styles.loginFormWrapper}>
        <h2>Store Owner Login</h2>
        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <div className={styles.formGroup}>
            <label htmlFor="username">Username</label>
            <input 
              type="text" 
              id="username" 
              value={username} 
              onChange={(e) => setUsername(e.target.value)} 
              required 
              className={styles.formInput}
              disabled={isLoading}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="password">Password</label>
            <input 
              type="password" 
              id="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              className={styles.formInput}
              disabled={isLoading}
            />
          </div>
          {error && <p className={styles.errorMessage}>{error}</p>}
          <button type="submit" disabled={isLoading} className={styles.submitButton}>
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage; 